import React, { useEffect, useState, useMemo, useCallback } from "react";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Icon,
  PaginationComponent,
  Row,
  Col,
  RSelect,
} from "../../components/Component";
import {
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  DropdownItem,
  DropdownMenu,
  UncontrolledDropdown,
  DropdownToggle,
} from "reactstrap";
import { handleGetApis, handlePostApis, handleDeleteApis } from "../../service/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const STATUS_COLOR  = { pending: "warning", approved: "success", rejected: "danger" };
const TYPE_COLOR    = { online: "info", offline: "secondary" };
const ITEMS_PER_PAGE = 20;

const STAR_ICONS = (rating) =>
  [1, 2, 3, 4, 5].map((s) => (
    <em
      key={s}
      className={`icon ni ni-star${s <= rating ? "-fill" : ""}`}
      style={{ color: s <= rating ? "#f4bd0e" : "#c8d4e7", fontSize: 13 }}
    />
  ));

const formatDate = (d) =>
  new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });

// ── Offline Review Form (shared) ───────────────────────────────────────────────

const defaultOfflineProduct = { product_id: "", reviewer_name: "", reviewer_email: "", rating: 5, title: "", review: "" };
const defaultOfflineBrand   = { reviewer_name: "", reviewer_email: "", rating: 5, title: "", review: "" };

// ── Component ─────────────────────────────────────────────────────────────────

const ReviewList = () => {
  // Tab: "product" | "brand"
  const [tab, setTab] = useState("product");

  // Reviews data
  const [reviews, setReviews]           = useState([]);
  const [pagination, setPagination]     = useState({ total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading]           = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter]     = useState("");
  const [currentPage, setCurrentPage]   = useState(1);

  // Action loading
  const [actionId, setActionId]         = useState(null);

  // Detail modal
  const [detailOpen, setDetailOpen]     = useState(false);
  const [detailReview, setDetailReview] = useState(null);

  // Delete confirm modal
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Offline add modal
  const [offlineOpen, setOfflineOpen]   = useState(false);
  const [offlineForm, setOfflineForm]   = useState(defaultOfflineProduct);
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [products, setProducts]         = useState([]);

  // Fetch all products for offline selection
  const fetchProductsList = async () => {
    try {
      const res = await handleGetApis("admin/get/products");
      if (res?.data?.success) {
        const formatted = (res.data.data || []).map((p) => ({
          value: p.id,
          label: `${p.name} (ID: #${p.id})`,
        }));
        setProducts(formatted);
      }
    } catch (err) {
      console.error("Failed to load products list:", err);
    }
  };

  useEffect(() => {
    fetchProductsList();
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchReviews = useCallback(async (pg = currentPage) => {
    setLoading(true);
    try {
      const endpoint = tab === "product" ? "reviews/product" : "reviews/brand";
      const params   = new URLSearchParams({ page: pg, limit: ITEMS_PER_PAGE });
      if (statusFilter) params.append("status", statusFilter);
      if (typeFilter)   params.append("type",   typeFilter);

      const res = await handleGetApis(`admin/${endpoint}?${params}`);
      if (res?.data?.success) {
        setReviews(res.data.data || []);
        setPagination(res.data.pagination || { total: 0, page: pg, total_pages: 1 });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }, [tab, statusFilter, typeFilter, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchReviews(1);
    // Reset offline form on tab switch
    setOfflineForm(tab === "product" ? { ...defaultOfflineProduct } : { ...defaultOfflineBrand });
  }, [tab, statusFilter, typeFilter]); // eslint-disable-line

  useEffect(() => {
    fetchReviews(currentPage);
  }, [currentPage]); // eslint-disable-line

  // ── Summary counts (from current page data — use pagination.total for total) ─

  const counts = useMemo(() => {
    const pending  = reviews.filter((r) => r.status === "pending").length;
    const approved = reviews.filter((r) => r.status === "approved").length;
    const rejected = reviews.filter((r) => r.status === "rejected").length;
    return { pending, approved, rejected };
  }, [reviews]);

  // ── Status Update ──────────────────────────────────────────────────────────

  const updateStatus = async (id, status) => {
    setActionId(id);
    try {
      const endpoint = tab === "product"
        ? "admin/reviews/product/status"
        : "admin/reviews/brand/status";

      const res = await handlePostApis(endpoint, { id, status });
      if (res?.data?.success) {
        setReviews((prev) => prev.map((r) => r.id === id ? { ...r, status } : r));
        if (detailReview?.id === id) setDetailReview((p) => ({ ...p, status }));
        toast.success(`Review ${status}`);
      } else {
        toast.error(res?.data?.message || "Failed to update status");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setActionId(null);
    }
  };

  // ── Delete ─────────────────────────────────────────────────────────────────

  const openDelete = (review) => { setDeleteTarget(review); setDeleteOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const endpoint = tab === "product"
        ? "admin/reviews/product"
        : "admin/reviews/brand";

      const res = await handleDeleteApis(endpoint, { id: deleteTarget.id });
      if (res?.data?.success || res?.status === 200) {
        setReviews((prev) => prev.filter((r) => r.id !== deleteTarget.id));
        setPagination((p) => ({ ...p, total: p.total - 1 }));
        toast.success("Review deleted");
        setDeleteOpen(false);
        if (detailReview?.id === deleteTarget.id) setDetailOpen(false);
      } else {
        toast.error(res?.data?.message || "Failed to delete");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Add Offline Review ─────────────────────────────────────────────────────

  const submitOffline = async () => {
    const f = offlineForm;

    // Validate
    if (tab === "product" && !f.product_id) return toast.error("Product ID required");
    if (!f.reviewer_name.trim()) return toast.error("Reviewer name required");
    if (!f.reviewer_email.trim()) return toast.error("Reviewer email required");
    if (!f.review.trim())        return toast.error("Review text required");
    if (f.rating < 1 || f.rating > 5) return toast.error("Rating must be 1-5");

    setOfflineLoading(true);
    try {
      const endpoint = tab === "product"
        ? "admin/reviews/product/offline"
        : "admin/reviews/brand/offline";

      const res = await handlePostApis(endpoint, f);
      if (res?.data?.success) {
        toast.success("Offline review added & approved");
        setOfflineOpen(false);
        setOfflineForm(tab === "product" ? { ...defaultOfflineProduct } : { ...defaultOfflineBrand });
        fetchReviews(1);
        setCurrentPage(1);
      } else {
        toast.error(res?.data?.message || "Failed to add review");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setOfflineLoading(false);
    }
  };

  const resetFilters = () => { setStatusFilter(""); setTypeFilter(""); setCurrentPage(1); };
  const isFiltered   = statusFilter || typeFilter;

  // ── UI ─────────────────────────────────────────────────────────────────────

  return (
    <React.Fragment>
      <Head title="Reviews" />
      <ToastContainer position="top-right" autoClose={3000} />

      <Content>

        {/* ── Page Header ─────────────────────────────────────── */}
        <BlockHead size="sm">
          <BlockBetween className="flex-wrap gap-2">
            <BlockHeadContent>
              <BlockTitle>
                Reviews{" "}
                <small className="text-muted fs-14px">
                  ({pagination.total} total)
                </small>
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <Button color="primary" size="sm" outline onClick={() => setOfflineOpen(true)}>
                  <Icon name="plus" /><span className="ms-1">Add Offline Review</span>
                </Button>
                <Button color="light" size="sm" onClick={() => fetchReviews(currentPage)} disabled={loading}>
                  <Icon name="reload" /><span className="ms-1">Refresh</span>
                </Button>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>

          {/* ── Tabs ────────────────────────────────────────────── */}
          <div className="d-flex mb-4" style={{ borderBottom: "2px solid #e5e9f2" }}>
            {[
              { key: "product", label: "Product Reviews", icon: "package" },
              { key: "brand",   label: "Brand Reviews",   icon: "star"    },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className="btn"
                style={{
                  padding: "10px 20px",
                  border: "none",
                  background: "transparent",
                  fontWeight: 600,
                  fontSize: 14,
                  color: tab === t.key ? "#6576ff" : "#8094ae",
                  borderBottom: tab === t.key ? "3px solid #6576ff" : "3px solid transparent",
                  borderRadius: 0,
                  marginBottom: -2,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "none",
                }}
              >
                <em className={`icon ni ni-${t.icon}`} style={{ fontSize: 16 }} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* ── Summary Cards ────────────────────────────────────── */}
          <Row className="g-3 mb-3">
            {[
              { label: "Total",    value: pagination.total, icon: "chat-fill",    color: "primary" },
              { label: "Pending",  value: counts.pending,   icon: "clock",        color: "warning" },
              { label: "Approved", value: counts.approved,  icon: "check-circle", color: "success" },
              { label: "Rejected", value: counts.rejected,  icon: "cross-circle", color: "danger"  },
            ].map((c) => (
              <Col sm={6} lg={3} key={c.label}>
                <div className="card card-bordered h-100">
                  <div className="card-inner py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-soft mb-1" style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          {c.label}
                        </p>
                        <h4 className={`mb-0 text-${c.color}`} style={{ fontWeight: 700 }}>{c.value}</h4>
                      </div>
                      <em className={`icon ni ni-${c.icon} text-${c.color}`} style={{ fontSize: 28, opacity: 0.3 }} />
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>

          {/* ── Filter Card ──────────────────────────────────────── */}
          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <Row className="g-3 align-items-end">
                <Col sm={6} md={4}>
                  <label className="form-label">Status</label>
                  <select className="form-select form-control" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </Col>
                <Col sm={6} md={4}>
                  <label className="form-label">Type</label>
                  <select className="form-select form-control" value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}>
                    <option value="">All Types</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>
                </Col>
                <Col sm={6} md={4}>
                  {isFiltered && (
                    <Button color="light" outline onClick={resetFilters} className="w-100">
                      <Icon name="cross-circle" /><span className="ms-1">Reset Filters</span>
                    </Button>
                  )}
                </Col>
              </Row>
            </div>
          </div>

          {/* ── Table ───────────────────────────────────────────── */}
          <div className="card card-bordered">
            <div className="card-inner p-0">
              {loading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner color="primary" />
                  <span className="ms-2 text-soft">Loading reviews…</span>
                </div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-5">
                  <em className="icon ni ni-chat-fill" style={{ fontSize: 48, color: "#c8d4e7" }} />
                  <p className="text-soft mt-2">No reviews found.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-tranx nk-tb-list nk-tb-ulist">
                    <DataTableHead>
                      <DataTableRow className="nk-tb-col-check"><span>#</span></DataTableRow>
                      <DataTableRow><span>Reviewer</span></DataTableRow>
                      {tab === "product" && <DataTableRow><span>Product</span></DataTableRow>}
                      <DataTableRow><span>Rating</span></DataTableRow>
                      <DataTableRow><span>Review</span></DataTableRow>
                      <DataTableRow><span>Type</span></DataTableRow>
                      <DataTableRow><span>Status</span></DataTableRow>
                      <DataTableRow><span>Date</span></DataTableRow>
                      <DataTableRow className="nk-tb-col-tools"><span>Actions</span></DataTableRow>
                    </DataTableHead>

                  {reviews.map((r, idx) => (
                    <DataTableItem key={r.id}>
                      {/* # */}
                      <DataTableRow className="nk-tb-col-check">
                        <span className="text-soft fw-bold" style={{ fontSize: 12 }}>
                          {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                        </span>
                      </DataTableRow>

                      {/* Reviewer */}
                      <DataTableRow>
                        <div className="user-info">
                          <span className="tb-lead" style={{ fontSize: 13 }}>
                            {r.reviewer_name || "Anonymous"}
                          </span>
                        </div>
                      </DataTableRow>

                      {/* Product name — only for product tab */}
                      {tab === "product" && (
                        <DataTableRow>
                          <div>
                            <span className="tb-lead" style={{ fontSize: 12 }}>
                              {r.product_name || "—"}
                            </span>
                          </div>
                        </DataTableRow>
                      )}

                      {/* Rating */}
                      <DataTableRow>
                        <div className="d-flex align-items-center gap-1">
                          {/* {STAR_ICONS(r.rating)} */}
                          <span className="text-soft ms-1" style={{ fontSize: 11 }}>({r.rating})</span>
                        </div>
                      </DataTableRow>

                      {/* Review */}
                      <DataTableRow>
                        <span
                          className="text-soft"
                          style={{ fontSize: 12, maxWidth: 220, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={r.review}
                        >
                          {r.title && <strong>{r.title}: </strong>}
                          {r.review}
                        </span>
                      </DataTableRow>

                      {/* Type */}
                      <DataTableRow>
                        <Badge color={TYPE_COLOR[r.type] || "light"} className="text-capitalize">
                          {r.type || "online"}
                        </Badge>
                      </DataTableRow>

                      {/* Status */}
                      <DataTableRow>
                        <Badge color={STATUS_COLOR[r.status]} className="text-capitalize">
                          {r.status}
                        </Badge>
                      </DataTableRow>

                      {/* Date */}
                      <DataTableRow>
                        <span className="text-soft" style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                          {formatDate(r.created_at)}
                        </span>
                      </DataTableRow>

                      {/* Actions */}
                    
                      <DataTableRow className="nk-tb-col-tools text-end">
                                 <ul className="nk-tb-actions gx-1">
                                   <li>
                                     <UncontrolledDropdown>
                                       <DropdownToggle tag="a" className="text-soft dropdown-toggle btn btn-icon btn-trigger">
                                         <Icon name="more-h"></Icon>
                                       </DropdownToggle>
                                       <DropdownMenu end>
                                         <ul className="link-list-opt no-bdr">
                                           <li onClick={() => { setDetailReview(r); setDetailOpen(true); }}>
                                             <DropdownItem
                                               tag="a"
                                               href="#edit"
                                               onClick={(ev) => {
                                                 ev.preventDefault();
                                               }}
                                             >
                                               <Icon name="eye"></Icon>
                                               <span>View</span>
                                             </DropdownItem>
                                           </li>
                                           {r.status !== "approved" && (  <li disabled={actionId === r.id} onClick={() => updateStatus(r.id, "approved")}>
                                               <DropdownItem
                                                 tag="a"
                                                 href="#markasdone"
                                                 onClick={(ev) => {
                                                   ev.preventDefault();
                                                 }}
                                               >
                                                 {actionId === r.id ? <Spinner size="sm" /> : <Icon name="check" />}
                                                 <span>Approve</span>
                                               </DropdownItem>
                                             </li>)}
                                            {r.status !== "rejected" && ( <li disabled={actionId === r.id}
                              onClick={() => updateStatus(r.id, "rejected")}>
                                             <DropdownItem
                                               tag="a"
                                               href="#edit"
                                               onClick={(ev) => {
                                                 ev.preventDefault();
                                               }}
                                             >
                                               <Icon name="cross"></Icon>
                                               <span>Reject</span>
                                             </DropdownItem>
                                           </li>)}
                                       {r.status !== "pending" && ( <li disabled={actionId === r.id}
                              onClick={() => updateStatus(r.id, "pending")}>
                                             <DropdownItem
                                               tag="a"
                                               href="#edit"
                                               onClick={(ev) => {
                                                 ev.preventDefault();
                                               }}
                                             >
                                               <Icon name="reload"></Icon>
                                               <span>Pending</span>
                                             </DropdownItem>
                                           </li>)}
                                           <li onClick={() => openDelete(r)}>
                                             <DropdownItem
                                               tag="a"
                                               href="#edit"
                                               onClick={(ev) => {
                                                 ev.preventDefault();
                                               }}
                                             >
                                               <Icon name="trash"></Icon>
                                               <span>Delete</span>
                                             </DropdownItem>
                                           </li>
                                         </ul>
                                       </DropdownMenu>
                                     </UncontrolledDropdown>
                                   </li>
                                 </ul>
                               </DataTableRow>
                    </DataTableItem>
                 
              ))}
               </table>
                </div>
              )}
            </div>

            {/* Pagination */}
            {!loading && pagination.total_pages > 1 && (
              <div className="card-inner">
                <PaginationComponent
                  itemPerPage={ITEMS_PER_PAGE}
                  totalItems={pagination.total}
                  paginate={(p) => setCurrentPage(p)}
                  currentPage={currentPage}
                />
              </div>
            )}
          </div>

        </Block>
      </Content>

      {/* ══════════════════════════════════════════════════════════
          DETAIL MODAL
      ══════════════════════════════════════════════════════════ */}
      {detailReview && (
        <Modal isOpen={detailOpen} toggle={() => setDetailOpen(false)} size="lg">
          <ModalHeader toggle={() => setDetailOpen(false)}>
            Review — {detailReview.reviewer_name || "Anonymous"}
          </ModalHeader>
          <ModalBody>
            <Row className="g-3">
              <Col md={6}>
                <table className="table table-sm table-bordered">
                  <tbody>
                    <tr>
                      <td className="text-soft fw-bold" style={{ width: 130 }}>Reviewer</td>
                      <td>{detailReview.reviewer_name || "—"}</td>
                    </tr>
                    {detailReview.user_id && (
                      <tr>
                        <td className="text-soft fw-bold">User ID</td>
                        <td>#{detailReview.user_id}</td>
                      </tr>
                    )}
                    {tab === "product" && (
                      <tr>
                        <td className="text-soft fw-bold">Product</td>
                        <td>{detailReview.product_name || "—"} <span className="text-soft">(#{detailReview.product_id})</span></td>
                      </tr>
                    )}
                    <tr>
                      <td className="text-soft fw-bold">Type</td>
                      <td><Badge color={TYPE_COLOR[detailReview.type] || "light"} className="text-capitalize">{detailReview.type || "online"}</Badge></td>
                    </tr>
                    <tr>
                      <td className="text-soft fw-bold">Date</td>
                      <td>{formatDate(detailReview.created_at)}</td>
                    </tr>
                    {tab === "product" && (
                      <tr>
                        <td className="text-soft fw-bold">Verified Purchase</td>
                        <td>{detailReview.is_verified_purchase ? "✓ Yes" : "No"}</td>
                      </tr>
                    )}
                    {tab === "brand" && (
                      <tr>
                        <td className="text-soft fw-bold">Verified Customer</td>
                        <td>{detailReview.is_verified_customer ? "✓ Yes" : "No"}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </Col>

              <Col md={6}>
                <div className="mb-3">
                  <p className="text-soft fw-bold mb-1" style={{ fontSize: 12 }}>Rating</p>
                  <div className="d-flex align-items-center gap-1">
                    {STAR_ICONS(detailReview.rating)}
                    <span className="ms-1 text-soft">({detailReview.rating}/5)</span>
                  </div>
                </div>
                <div className="mb-3">
                  <p className="text-soft fw-bold mb-1" style={{ fontSize: 12 }}>Status</p>
                  <Badge color={STATUS_COLOR[detailReview.status]} className="text-capitalize">
                    {detailReview.status}
                  </Badge>
                </div>
              </Col>

              {detailReview.title && (
                <Col md={12}>
                  <p className="text-soft fw-bold mb-1" style={{ fontSize: 12 }}>Title</p>
                  <p className="mb-0 fw-bold">{detailReview.title}</p>
                </Col>
              )}

              <Col md={12}>
                <p className="text-soft fw-bold mb-1" style={{ fontSize: 12 }}>Review Text</p>
                <div className="card bg-lighter p-3" style={{ borderRadius: 8, fontSize: 14, color: "#364a63", lineHeight: 1.7 }}>
                  "{detailReview.review}"
                </div>
              </Col>

              {/* Action buttons */}
              <Col md={12}>
                <div className="d-flex gap-2 flex-wrap">
                  {detailReview.status !== "approved" && (
                    <Button color="success" size="sm" disabled={!!actionId}
                      onClick={() => updateStatus(detailReview.id, "approved")}>
                      <Icon name="check-circle" /><span className="ms-1">Approve</span>
                    </Button>
                  )}
                  {detailReview.status !== "rejected" && (
                    <Button color="warning" size="sm" disabled={!!actionId}
                      onClick={() => updateStatus(detailReview.id, "rejected")}>
                      <Icon name="cross-circle" /><span className="ms-1">Reject</span>
                    </Button>
                  )}
                  {detailReview.status !== "pending" && (
                    <Button color="info" size="sm" disabled={!!actionId}
                      onClick={() => updateStatus(detailReview.id, "pending")}>
                      <Icon name="reload" /><span className="ms-1">Set Pending</span>
                    </Button>
                  )}
                  <Button color="danger" size="sm"
                    onClick={() => { setDetailOpen(false); openDelete(detailReview); }}>
                    <Icon name="trash" /><span className="ms-1">Delete</span>
                  </Button>
                </div>
              </Col>
            </Row>
          </ModalBody>
        </Modal>
      )}

      {/* ══════════════════════════════════════════════════════════
          DELETE CONFIRM MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal isOpen={deleteOpen} toggle={() => !deleteLoading && setDeleteOpen(false)} size="sm">
        <ModalHeader toggle={() => !deleteLoading && setDeleteOpen(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p className="mb-0">
            Are you sure you want to delete this review by{" "}
            <strong>{deleteTarget?.reviewer_name || "Anonymous"}</strong>?
            This action cannot be undone.
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete} disabled={deleteLoading}>
            {deleteLoading ? <><Spinner size="sm" className="me-1" />Deleting…</> : "Yes, Delete"}
          </Button>
          <Button color="light" onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* ══════════════════════════════════════════════════════════
          ADD OFFLINE REVIEW MODAL
      ══════════════════════════════════════════════════════════ */}
      <Modal isOpen={offlineOpen} toggle={() => !offlineLoading && setOfflineOpen(false)} size="md">
        <ModalHeader toggle={() => !offlineLoading && setOfflineOpen(false)}>
          Add Offline {tab === "product" ? "Product" : "Brand"} Review
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            {tab === "product" && (
              <Col md={12}>
                <label className="form-label">Select Product <span className="text-danger">*</span></label>
                <RSelect
                  options={products}
                  placeholder="Search and select product..."
                  value={products.find((p) => p.value === Number(offlineForm.product_id))}
                  onChange={(e) => setOfflineForm({ ...offlineForm, product_id: e ? String(e.value) : "" })}
                />
              </Col>
            )}
            <Col md="6">
              <label className="form-label">Reviewer Name <span className="text-danger">*</span></label>
              <input
                className="form-control"
                placeholder="Customer name"
                value={offlineForm.reviewer_name}
                onChange={(e) => setOfflineForm({ ...offlineForm, reviewer_name: e.target.value })}
              />
            </Col>
            <Col md="6">
              <label className="form-label">Reviewer Email <span className="text-danger">*</span></label>
              <input
                className="form-control"
                type="email"
                placeholder="Customer email"
                value={offlineForm.reviewer_email}
                onChange={(e) => setOfflineForm({ ...offlineForm, reviewer_email: e.target.value })}
              />
            </Col>
            <Col md="12">
              <label className="form-label">Rating <span className="text-danger">*</span></label>
              <select
                className="form-select form-control"
                value={offlineForm.rating}
                onChange={(e) => setOfflineForm({ ...offlineForm, rating: Number(e.target.value) })}
              >
                {[5, 4, 3, 2, 1].map((r) => (
                  <option key={r} value={r}>{"★".repeat(r)} ({r})</option>
                ))}
              </select>
            </Col>
            <Col md={12}>
              <label className="form-label">Title</label>
              <input
                className="form-control"
                placeholder="Review title (optional)"
                value={offlineForm.title}
                onChange={(e) => setOfflineForm({ ...offlineForm, title: e.target.value })}
              />
            </Col>
            <Col md={12}>
              <label className="form-label">Review <span className="text-danger">*</span></label>
              <textarea
                className="form-control"
                rows={4}
                placeholder="Write the customer's review…"
                value={offlineForm.review}
                onChange={(e) => setOfflineForm({ ...offlineForm, review: e.target.value })}
              />
            </Col>
            <Col md={12}>
              <div className="alert alert-info py-2 px-3 mb-0" style={{ fontSize: 12 }}>
                <Icon name="info" /> Offline reviews are auto-approved and marked as <strong>type: offline</strong>.
              </div>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={submitOffline} disabled={offlineLoading}>
            {offlineLoading ? <><Spinner size="sm" className="me-1" />Adding…</> : "Add & Approve"}
          </Button>
          <Button color="light" onClick={() => setOfflineOpen(false)} disabled={offlineLoading}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
};

export default ReviewList;
